import axios from 'axios'
import { STORAGE_KEYS } from '@/api-common'

// ============================================================================
// API CONFIGURATION
// ============================================================================
const atarrayaApiAuth = axios.create({
  baseURL: process.env.VUE_APP_ATARRAYA_API_URL + '/v3/identity',
  timeout: process.env.VUE_APP_ATARRAYA_TIMEOUT
})

const API_ENDPOINTS = {
  LOGIN: '/auth/login'
}

const DEFAULT_LANGUAGE = 'sp'

// ============================================================================
// STATE
// ============================================================================
export const state = {
  token: null,
  userDisplayName: null,
  simpleFarms: []
}

// ============================================================================
// MUTATIONS
// ============================================================================
export const mutations = {
  LOGIN_SUCCESS(state, { userDisplayName, token }) {
    state.userDisplayName = userDisplayName
    state.token = token
  },

  LOGOUT(state) {
    state.userDisplayName = null
    state.token = null
    state.simpleFarms = []
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const parseJwtPayload = (token) => {
  try {
    const base64Payload = token.split('.')[1]
    return JSON.parse(Buffer.from(base64Payload, 'base64'))
  } catch (error) {
    console.error('Error parsing JWT payload:', error)
    return null
  }
}

const generateAvatar = (name, lastName) => {
  const firstInitial = name?.charAt(0) || ''
  const lastInitial = lastName?.charAt(0) || ''
  return `${firstInitial} ${lastInitial}`.trim()
}

const setAuthHeaders = (token) => {
  atarrayaApiAuth.defaults.headers['Authorization'] = `Bearer: ${token}`
}

const clearAuthHeaders = () => {
  delete atarrayaApiAuth.defaults.headers['Authorization']
}

const saveUserDataToStorage = (payload, token) => {
  const fullName = `${payload.name} ${payload.lastName}`
  const avatar = generateAvatar(payload.name, payload.lastName)

  const storageData = {
    [STORAGE_KEYS.USER]: fullName,
    [STORAGE_KEYS.EMAIL]: payload.email,
    [STORAGE_KEYS.LANGUAGE]: DEFAULT_LANGUAGE,
    [STORAGE_KEYS.AVATAR]: avatar,
    [STORAGE_KEYS.PRODUCER]: payload.producer,
    [STORAGE_KEYS.TOKEN]: token,
    [STORAGE_KEYS.EXPIRES_IN]: payload.exp
  }

  Object.entries(storageData).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      localStorage.setItem(key, value)
    }
  })

  return fullName
}

const clearStorageData = () => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

// ============================================================================
// ACTIONS
// ============================================================================
export const actions = {
  async login({ commit }, credentials) {
    try {
      const response = await atarrayaApiAuth.post(API_ENDPOINTS.LOGIN, credentials, {
        headers: { 'x-api-key': process.env.VUE_APP_ATARRAYA_API_KEY }
      })

      const token = response.data?.data?.accessToken
      if (!token) {
        throw new Error('No access token received')
      }

      const payload = parseJwtPayload(token)
      if (!payload) {
        throw new Error('Invalid token payload')
      }

      const userDisplayName = saveUserDataToStorage(payload, token)
      setAuthHeaders(token)

      commit('LOGIN_SUCCESS', {
        userDisplayName,
        token
      })

      return response
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  },

  logout({ commit, dispatch }) {
    clearStorageData()
    clearAuthHeaders()

    commit('LOGOUT')
    dispatch('farm/clearAllFarmsData', null, { root: true })
  }
}

// ============================================================================
// GETTERS
// ============================================================================
export const getters = {
  isAuthenticated: state => !!state.token,
  currentUser: state => state.userDisplayName,
  authToken: state => state.token
}